import * as React from 'react'
import logger from '@/helpers/logger'

/** A single fix from the device, reduced to what the map actually consumes. */
export interface Fix {
  latitude: number
  longitude: number
  /** Radius of the 95% confidence circle, in meters, as reported by the device. */
  accuracyM: number
  takenAt: number
}

export type GeolocationStatus = GeolocationState['status']

/**
 * Where the watch is, and the fix or the reason, together.
 *
 * A tracking device has a fix. A refusal has a message and no fix. Keeping
 * those as separate fields let a caller treat "tracking" and "no fix" as a
 * combination that cannot happen, and then write a caption for it.
 */
export type GeolocationState =
  | { status: 'idle'; fix: null; message: null }
  | { status: 'locating'; fix: Fix | null; message: null }
  | { status: 'tracking'; fix: Fix; message: null }
  | {
      status: 'unsupported' | 'insecure' | 'denied' | 'unavailable'
      fix: null
      message: string
    }

/**
 * Accuracy beyond which a fix should not be trusted to place a pin.
 *
 * Trees on the reference site maps sit roughly 8 m apart, so a fix worse than
 * this cannot distinguish one tree from its neighbor. The number is not a
 * failure threshold — it is the point at which the UI has to stop implying the
 * dot is where you are standing and start asking for a manual correction.
 */
export const ACCURACY_WARN_M = 10

/**
 * Track the device's position for the life of the screen.
 *
 * `watchPosition`, not `getCurrentPosition`: a phone's first fix is frequently
 * its worst — a cell-tower estimate hundreds of meters wide — and it tightens
 * over the following seconds as GPS satellites are acquired. Taking one reading
 * and stopping bakes in that first bad guess.
 *
 * `enableHighAccuracy` asks for the GPS chip rather than the network estimate.
 * It costs battery, which is the correct trade for a screen someone opens
 * specifically to record where they are standing.
 */
export function useGeolocation(enabled: boolean): GeolocationState {
  const [state, setState] = React.useState<GeolocationState>({
    status: 'idle',
    fix: null,
    message: null,
  })

  React.useEffect(() => {
    if (!enabled) return undefined

    /*
     * Geolocation is gated on a secure context. `localhost` qualifies, but a
     * phone pointed at this dev server over the LAN (http://192.168.x.x:3010)
     * does NOT — and the API is simply absent rather than throwing, which reads
     * as a broken app. Name it explicitly so the fix (a tunnel, or the deployed
     * origin) is obvious instead of being discovered an hour later.
     */
    if (!window.isSecureContext) {
      setState({
        status: 'insecure',
        fix: null,
        message:
          'Location needs a secure origin. Open this over HTTPS (or on localhost) — a plain-http LAN address cannot use GPS.',
      })
      return undefined
    }

    if (!('geolocation' in navigator)) {
      setState({
        status: 'unsupported',
        fix: null,
        message: 'This browser cannot report a location.',
      })
      return undefined
    }

    setState((current) => ({
      status: 'locating',
      // Keep the last fix while the next one is on its way. The message from
      // a previous refusal does not survive: locating is not an error.
      fix: current.fix,
      message: null,
    }))

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setState({
          status: 'tracking',
          fix: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyM: position.coords.accuracy,
            takenAt: position.timestamp,
          },
          message: null,
        })
      },
      (error) => {
        const denied = error.code === error.PERMISSION_DENIED
        logger.warn('field-map: geolocation failed', {
          code: error.code,
          reason: error.message,
        })
        setState({
          status: denied ? 'denied' : 'unavailable',
          fix: null,
          message: denied
            ? 'Location permission is blocked. Allow it in your browser settings to place pins where you are standing.'
            : 'Could not get a location fix. You can still place pins by hand.',
        })
      },
      {
        enableHighAccuracy: true,
        /*
         * Never hand back a cached fix. The default allows a position from
         * minutes ago, which on this screen means dropping a pin at the last
         * tree rather than this one.
         */
        maximumAge: 0,
        timeout: 20_000,
      },
    )

    return () => {
      navigator.geolocation.clearWatch(watchId)
    }
  }, [enabled])

  return state
}
